import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Copy,
  MessageCircle,
  Smartphone,
  QrCode as QrIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/site";
import { getAttribution } from "@/lib/utm";

// Structured message template — the prospect fills the blanks inside WhatsApp
const buildMessage = () => {
  const attr = getAttribution();
  const src = attr.utm_source ? `\n(Ref: ${attr.utm_source}${attr.utm_campaign ? " · " + attr.utm_campaign : ""})` : "";
  return (
    `Hi Raj 👋 I'd like to apply for a loan through Swift Financial. Here are my details — please review and let me know the next steps.

1. Name: 
2. Mobile: 
3. Email: 
4. City: 

5. Loan type (Home / Business / LAP / Personal / Balance Transfer / Project Finance): 
6. Loan amount needed: 
7. Preferred tenure: 

8. Employment (Salaried / Self-Employed / Business Owner / Property Investor): 
9. Monthly take-home OR annual turnover: 
10. Existing EMIs (if any): 

11. Property / collateral available (Yes / No): 
12. Approx. property value (if Yes): 

13. Anything else I should mention: 
` + src
  );
};

const STEPS = [
  {
    n: "1",
    t: "Tap the WhatsApp button",
    d: "Opens a new WhatsApp chat with Raj — the template is pre-filled with the exact questions you need to answer.",
  },
  {
    n: "2",
    t: "Fill your details next to each number",
    d: "Right inside WhatsApp — no forms, no website, no login. Takes about 90 seconds. Skip anything you're not sure about.",
  },
  {
    n: "3",
    t: "Hit send",
    d: "Raj gets it instantly, reviews and calls you back within one business day with the best-fit lender options.",
  },
];

export default function ApplyWhatsApp() {
  const [copied, setCopied] = useState(false);
  const trackedRef = useRef(false);
  const message = buildMessage();
  const waUrl = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(message)}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=8&data=${encodeURIComponent(waUrl)}`;

  // Fire-and-forget: on first click of the primary CTA, record the WhatsApp intent as a lead
  // (apply_method='whatsapp_redirect') so Raj can reconcile against his WhatsApp inbox.
  const trackWhatsAppIntent = () => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    try {
      const attr = getAttribution();
      api.post("/apply/track-whatsapp", {
        persona: attr.persona,
        landing_page: attr.landing_page || window.location.pathname,
        utm_source: attr.utm_source,
        utm_medium: attr.utm_medium,
        utm_campaign: attr.utm_campaign,
        utm_content: attr.utm_content,
        utm_term: attr.utm_term,
        referrer: attr.referrer,
      }).catch(() => {
        // silent — don't disrupt the WhatsApp handoff on tracking failure
      });
    } catch (e) {
      // silent
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(message).then(
      () => {
        setCopied(true);
        toast.success("Template copied — paste into WhatsApp");
        setTimeout(() => setCopied(false), 2500);
      },
      () => toast.error("Copy failed — long-press the box to select and copy manually")
    );
  };

  return (
    <div data-testid="apply-whatsapp-page" className="container-x py-12 md:py-16">
      <div className="max-w-4xl mx-auto">
        <Link to="/apply" className="btn-ghost text-xs">
          <ArrowLeft className="h-3.5 w-3.5" /> All application methods
        </Link>

        <div className="mt-6 text-center">
          <span className="eyebrow">Apply on WhatsApp</span>
          <h1 className="mt-4 h-display text-4xl sm:text-5xl">
            Apply for your loan — directly on <span className="text-[#25D366]">WhatsApp</span>.
          </h1>
          <p className="mt-5 text-brand-sage text-lg max-w-2xl mx-auto leading-relaxed">
            No form to fill on the website. No password to remember. Just chat with Raj on
            WhatsApp — the way you'd message a friend. Three simple steps and you're done.
          </p>
        </div>

        {/* THREE STEPS */}
        <ol className="mt-14 grid md:grid-cols-3 gap-5" data-testid="wa-steps">
          {STEPS.map((s, i) => (
            <li key={s.n} data-testid={`wa-step-${i}`} className="card-soft p-6">
              <div className="grid h-10 w-10 place-items-center rounded-full bg-brand-terracotta text-white font-outfit font-extrabold">
                {s.n}
              </div>
              <h3 className="mt-4 font-outfit font-bold text-lg text-brand-forest">{s.t}</h3>
              <p className="mt-2 text-sm text-brand-sage leading-relaxed">{s.d}</p>
            </li>
          ))}
        </ol>

        {/* PRIMARY CTA + QR */}
        <div className="mt-12 rounded-3xl bg-brand-forest text-brand-cream p-8 md:p-10 relative overflow-hidden">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#25D366]/25 blur-2xl" />
          <div className="absolute -left-8 bottom-0 h-40 w-40 rounded-full bg-brand-gold/20 blur-2xl" />

          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <div className="text-xs uppercase tracking-[0.18em] text-brand-gold font-semibold">Start now</div>
              <h2 className="mt-3 font-outfit font-extrabold text-3xl sm:text-4xl tracking-tight">
                Open the WhatsApp chat with Raj — the template's already inside.
              </h2>
              <p className="mt-3 text-brand-cream/75 max-w-md">
                Just tap the button. Fill your details next to each question. Send.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={trackWhatsAppIntent}
                  data-testid="wa-primary-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-base font-semibold text-white shadow-[0_12px_28px_-10px_rgba(37,211,102,0.7)] hover:bg-[#1ea854] hover:-translate-y-0.5 transition-all"
                >
                  <MessageCircle className="h-5 w-5" /> Start on WhatsApp
                </a>
                <button
                  onClick={copy}
                  data-testid="wa-copy-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-cream/25 bg-brand-cream/5 backdrop-blur px-5 py-3 text-sm font-semibold text-brand-cream hover:bg-brand-cream/10 transition-all"
                >
                  {copied ? <><CheckCircle2 className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy template</>}
                </button>
              </div>

              <div className="mt-4 flex items-center gap-2 text-xs text-brand-cream/60">
                <Smartphone className="h-3.5 w-3.5" />
                <span>Best on mobile. On desktop, scan the QR →</span>
              </div>
            </div>

            {/* QR */}
            <div className="md:col-span-5">
              <div className="rounded-2xl bg-white p-4 mx-auto max-w-[280px]" data-testid="wa-qr-block">
                <img src={qrUrl} alt="Scan to open WhatsApp chat with Raj" className="w-full aspect-square" />
                <div className="mt-3 text-center">
                  <div className="flex items-center justify-center gap-1.5 text-xs uppercase tracking-[0.16em] font-semibold text-brand-terracotta">
                    <QrIcon className="h-3.5 w-3.5" /> Scan with phone
                  </div>
                  <div className="mt-1 text-[11px] text-brand-sage">Opens the same pre-filled chat on WhatsApp</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* MESSAGE PREVIEW */}
        <div className="mt-12">
          <div className="flex items-end justify-between gap-4">
            <div>
              <span className="eyebrow">Preview</span>
              <h2 className="mt-3 h-display text-2xl sm:text-3xl">This is what WhatsApp will open with.</h2>
              <p className="mt-2 text-brand-sage text-sm max-w-lg">
                You'll fill in your answers next to each number — right inside WhatsApp — before hitting send.
              </p>
            </div>
            <button
              onClick={copy}
              data-testid="wa-copy-btn-2"
              className="btn-ghost hidden sm:inline-flex text-xs"
            >
              <Copy className="h-3.5 w-3.5" /> Copy
            </button>
          </div>

          {/* Phone frame */}
          <div className="mt-6 mx-auto max-w-md rounded-[2.5rem] border-8 border-brand-forest bg-brand-forest shadow-[0_40px_100px_-30px_rgba(17,42,34,0.5)] overflow-hidden">
            <div className="bg-[#075E54] text-white px-4 py-3 flex items-center gap-3">
              <div className="grid h-9 w-9 place-items-center rounded-full bg-brand-gold text-brand-forest font-outfit font-extrabold text-sm">S</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">Raj · Swift Financial</div>
                <div className="text-[11px] text-white/70">online</div>
              </div>
              <MessageCircle className="h-5 w-5 text-white/80" />
            </div>
            <div
              className="bg-[#ECE5DD] px-4 py-5 min-h-[380px]"
              style={{
                backgroundImage:
                  "radial-gradient(rgba(17,42,34,0.05) 1px, transparent 1px)",
                backgroundSize: "14px 14px",
              }}
              data-testid="wa-msg-preview"
            >
              <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-[#D9FDD3] p-3 text-brand-forest text-sm leading-snug whitespace-pre-line shadow-sm ml-auto">
                {message}
              </div>
              <div className="mt-2 text-center">
                <span className="text-[10px] text-brand-sage">You'll fill the blanks and tap send ↑</span>
              </div>
            </div>
          </div>
        </div>

        {/* ALTERNATIVES */}
        <div className="mt-14 grid md:grid-cols-2 gap-4">
          <Link to="/apply-chat" data-testid="wa-alt-chat" className="card-soft p-5 flex items-center justify-between hover:-translate-y-0.5 transition-transform">
            <div>
              <div className="text-xs uppercase tracking-[0.14em] font-semibold text-brand-terracotta">Prefer chat on the website?</div>
              <div className="mt-1 font-outfit font-bold text-brand-forest">Try our on-site chat assistant</div>
              <div className="mt-1 text-xs text-brand-sage">Same conversational flow, right here — no WhatsApp needed.</div>
            </div>
            <ArrowRight className="h-5 w-5 text-brand-terracotta" />
          </Link>
          <Link to="/apply-form" data-testid="wa-alt-form" className="card-soft p-5 flex items-center justify-between hover:-translate-y-0.5 transition-transform">
            <div>
              <div className="text-xs uppercase tracking-[0.14em] font-semibold text-brand-terracotta">Prefer the classic form?</div>
              <div className="mt-1 font-outfit font-bold text-brand-forest">5-step application form</div>
              <div className="mt-1 text-xs text-brand-sage">More structured — best if you already have your numbers ready.</div>
            </div>
            <ArrowRight className="h-5 w-5 text-brand-terracotta" />
          </Link>
        </div>

        <p className="mt-10 text-center text-xs text-brand-sage max-w-lg mx-auto">
          Your details reach Raj directly — never sold or shared. Documents (PAN, Aadhaar, bank statements) are collected securely over WhatsApp / email after the first call.
        </p>
      </div>
    </div>
  );
}
